import { useEffect, useRef, useState } from 'react';

import { get } from 'lodash-es';

import { useAppSelector } from 'src/app/store/hooks';
import { DispatchTripRead } from 'src/common/external/bambi-api/bambiApi';

export type MultiloadPickupDropoffOrderItem = {
  // This will be the id of the trip + type
  id: string;
  trip: DispatchTripRead;
  type: 'pickup' | 'dropoff';
  // Used for initial order
  scheduledAt: string;
  order: number | null;
};

export function useMultiloadOrderItems() {
  const [orderedMultiLoadItems, setOrderedMultiLoadItems] = useState<
    MultiloadPickupDropoffOrderItem[]
  >([]);
  const { selectedTripIdsToMultiload, originatingTripId } = useAppSelector(
    (state) => state.dispatcher.multiloadAssignmentFlow
  );
  const unassignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.unassigned_trips
  );
  const assignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.assigned_trips
  );

  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current || !(unassignedTrips && assignedTrips)) return;
    initialized.current = true;
    const itemsToMultiload = [...unassignedTrips, ...assignedTrips].filter(
      (trip) =>
        selectedTripIdsToMultiload.includes(trip.id) ||
        trip.id === originatingTripId
    );
    setOrderedMultiLoadItems(orderMultiloadItems(itemsToMultiload));
  }, [unassignedTrips, assignedTrips, selectedTripIdsToMultiload, originatingTripId]);

  return { orderedMultiLoadItems, setOrderedMultiLoadItems };
}

export function orderMultiloadItems(
  items: DispatchTripRead[],
  idProperty = 'id'
) {
  const orderItems: MultiloadPickupDropoffOrderItem[] = [];
  items.forEach((item) => {
    orderItems.push({
      id: get(item, idProperty) + '-pickup',
      trip: item,
      type: 'pickup',
      scheduledAt: item.scheduled_pickup_at,
      order: item.pickup_order,
    });
    orderItems.push({
      id: get(item, idProperty) + '-dropoff',
      trip: item,
      type: 'dropoff',
      scheduledAt: item.scheduled_dropoff_at,
      order: item.dropoff_order,
    });
  });
  orderItems.sort((a, b) => {
    if (typeof a.order === 'number' && typeof b.order === 'number') {
      return a.order - b.order;
    }
    // Sort items with an order first
    if (typeof a.order === 'number' && typeof b.order !== 'number') {
      return -1;
    }
    if (typeof a.order !== 'number' && typeof b.order === 'number') {
      return 1;
    }
    return (
      new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
    );
  });

  return orderItems;
}
